import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { createOrUpdateItem } from '../../actions/createOrUpdateItem';
import { ItemsClient } from '../../api/ItemsClient';
import { LabelsClient } from '../../api/LabelsClient';
import { ModifierGroupsClient } from '../../api/ModifierGroupsClient';
import { ModifiersClient } from '../../api/ModifiersClient';
import { settleApiCall } from '../../api/utils';
import { PopulatedModifierGroupClient } from '../PopulatedModifierGroupClient/PopulatedModifierGroupClient';
import { buildPopulatedItem } from './actions/buildPopulatedItem';
import { getLabels } from './actions/getLabels';
import { getPopulatedModifierGroups } from './actions/getPopulatedModifierGroups';
import { getPriceVariants } from './actions/getPriceVariants';
import { duplicateItems } from '../../actions/duplicateItems';
import { VariantsClient } from '../../api/VariantsClient';
import { convertPopulatedItemToItemDTO } from '../../utils/converters';
export class PopulatedItemClient {
  constructor(_ref) {
    let {
      request,
      populationLevel
    } = _ref;
    _defineProperty(this, "populationLevel", void 0);
    _defineProperty(this, "itemsClient", void 0);
    _defineProperty(this, "labelsClient", void 0);
    _defineProperty(this, "modifiersClient", void 0);
    _defineProperty(this, "variantsClient", void 0);
    _defineProperty(this, "modifierGroupsClient", void 0);
    _defineProperty(this, "populatedModifierGroupsClient", void 0);
    this.populationLevel = populationLevel;
    this.itemsClient = new ItemsClient(request);
    this.labelsClient = new LabelsClient(request);
    this.modifiersClient = new ModifiersClient(request);
    this.variantsClient = new VariantsClient(request);
    this.modifierGroupsClient = new ModifierGroupsClient(request);
    const modifierGroupPopulationLevel = {
      modifiers: !!(populationLevel != null && populationLevel.modifiers)
    };
    this.populatedModifierGroupsClient = new PopulatedModifierGroupClient({
      request,
      populationLevel: modifierGroupPopulationLevel
    });
  }
  async createOrUpdate(_ref2) {
    let {
      item,
      isPriceVariantsToggled,
      itemModifierGroupsToCreate,
      itemPriceVariantsToCreate,
      source
    } = _ref2;
    const createOrUpdateItemResponse = await createOrUpdateItem({
      item,
      itemsClient: this.itemsClient,
      modifierGroupsClient: this.modifierGroupsClient,
      modifiersClient: this.modifiersClient,
      priceVariantsClient: this.variantsClient,
      itemModifierGroupsToCreate,
      itemPriceVariantsToCreate,
      populationLevel: this.populationLevel,
      isPriceVariantsToggled,
      source
    });
    if (createOrUpdateItemResponse.hasError) {
      return createOrUpdateItemResponse;
    }
    return {
      data: createOrUpdateItemResponse.data,
      hasError: false
    };
  }
  async getById(itemId) {
    const getItemResponse = await settleApiCall(() => this.itemsClient.getById(itemId));
    if (getItemResponse.hasError) {
      return getItemResponse;
    }
    const populateItemsResponse = await this.createPopulatedItems([getItemResponse.data]);
    if (populateItemsResponse.hasError) {
      return populateItemsResponse;
    }
    return {
      data: populateItemsResponse.data[0],
      hasError: false
    };
  }
  async getBulk(args) {
    const getItemsResponse = await settleApiCall(() => this.itemsClient.getBulk(args));
    if (getItemsResponse.hasError) {
      return getItemsResponse;
    }
    const {
      data: items,
      pagingMetadata
    } = getItemsResponse.data;
    const populateItemsResponse = await this.createPopulatedItems(items);
    if (populateItemsResponse.hasError) {
      return populateItemsResponse;
    }
    return {
      data: {
        data: populateItemsResponse.data,
        pagingMetadata
      },
      hasError: false
    };
  }
  async getOptimizedBulk(args) {
    var _args$ids;
    let getItemsResponse;
    if (!(args != null && (_args$ids = args.ids) != null && _args$ids.length) && !(args != null && args.filter)) {
      getItemsResponse = await settleApiCall(() => this.itemsClient.getListBulk({
        paging: args == null ? void 0 : args.paging,
        onlyVisible: args == null ? void 0 : args.onlyVisible
      }));
    } else {
      const filter = (args == null ? void 0 : args.onlyVisible) === undefined ? args == null ? void 0 : args.filter : {
        ...(args == null ? void 0 : args.filter),
        visible: {
          $eq: args == null ? void 0 : args.onlyVisible
        }
      };
      getItemsResponse = await settleApiCall(() => this.itemsClient.getBulk({
        ...args,
        filter
      }));
    }
    if (getItemsResponse.hasError) {
      return getItemsResponse;
    }
    const {
      data: items,
      pagingMetadata
    } = getItemsResponse.data;
    const populateItemsResponse = await this.createPopulatedItems(items);
    if (populateItemsResponse.hasError) {
      return populateItemsResponse;
    }
    return {
      data: {
        data: populateItemsResponse.data,
        pagingMetadata
      },
      hasError: false
    };
  }
  async getListBulk(args) {
    const getItemsResponse = await settleApiCall(() => this.itemsClient.getListBulk(args));
    if (getItemsResponse.hasError) {
      return getItemsResponse;
    }
    const {
      data: items,
      pagingMetadata
    } = getItemsResponse.data;
    const populateItemsResponse = await this.createPopulatedItems(items);
    if (populateItemsResponse.hasError) {
      return populateItemsResponse;
    }
    return {
      data: {
        data: populateItemsResponse.data,
        pagingMetadata
      },
      hasError: false
    };
  }
  async query(query) {
    const queryItemsResponse = await settleApiCall(() => this.itemsClient.query(query));
    if (queryItemsResponse.hasError) {
      return queryItemsResponse;
    }
    const populateItemsResponse = await this.createPopulatedItems(queryItemsResponse.data.data);
    if (populateItemsResponse.hasError) {
      return populateItemsResponse;
    }
    return {
      data: {
        data: populateItemsResponse.data,
        pagingMetadata: queryItemsResponse.data.pagingMetadata
      },
      hasError: false
    };
  }
  async countItems(filter, searchDetails) {
    const countItemResponse = await settleApiCall(() => this.itemsClient.countItems(filter, searchDetails));
    return countItemResponse;
  }
  async delete(itemId) {
    const deleteItemResponse = await settleApiCall(() => this.itemsClient.delete(itemId));
    if (deleteItemResponse.hasError) {
      return deleteItemResponse;
    }
    return;
  }
  async bulkDelete(itemIds) {
    const bulkDeleteItemsResponse = await settleApiCall(() => this.itemsClient.bulkDelete(itemIds));
    if (bulkDeleteItemsResponse.hasError) {
      return bulkDeleteItemsResponse;
    }
    return;
  }
  async bulkUpdate(items) {
    const itemsDto = items.map(item => {
      var _item$priceVariants;
      return convertPopulatedItemToItemDTO(item, !!((_item$priceVariants = item.priceVariants) != null && _item$priceVariants.variants), this.populationLevel);
    });
    const bulkUpdateItemsResponse = await settleApiCall(() => this.itemsClient.bulkUpdate(itemsDto));
    if (bulkUpdateItemsResponse.hasError) {
      return bulkUpdateItemsResponse;
    }
    return bulkUpdateItemsResponse;
  }
  async duplicate(_ref3) {
    let {
      items,
      duplicatedNameCreator,
      source
    } = _ref3;
    const duplicateItemsResponse = await duplicateItems({
      items,
      duplicatedNameCreator,
      itemsClient: this.itemsClient,
      populationLevel: this.populationLevel,
      source
    });
    return duplicateItemsResponse;
  }
  async search(cursorSearch) {
    const searchItemsResponse = await settleApiCall(() => this.itemsClient.search(cursorSearch));
    if (searchItemsResponse.hasError) {
      return searchItemsResponse;
    }
    const populateItemsResponse = await this.createPopulatedItems(searchItemsResponse.data.data);
    if (populateItemsResponse.hasError) {
      return populateItemsResponse;
    }
    return {
      data: {
        data: populateItemsResponse.data,
        pagingMetadata: searchItemsResponse.data.pagingMetadata
      },
      hasError: false
    };
  }
  async createPopulatedItems(items) {
    const [getLabelsResponse, getPriceVariantsResponse, populateModifierGroupsResponse] = await Promise.all([getLabels({
      labelsClient: this.labelsClient,
      items,
      populationLevel: this.populationLevel
    }), getPriceVariants({
      priceVariantsClient: this.variantsClient,
      modifiersClient: this.modifiersClient,
      items,
      populationLevel: this.populationLevel
    }), getPopulatedModifierGroups({
      modifierGroupsClient: this.modifierGroupsClient,
      populatedModifierGroupsClient: this.populatedModifierGroupsClient,
      items,
      populationLevel: this.populationLevel
    })]);
    if (getLabelsResponse.hasError || getPriceVariantsResponse.hasError || populateModifierGroupsResponse.hasError) {
      return [getLabelsResponse, getPriceVariantsResponse, populateModifierGroupsResponse].find(_ref4 => {
        let {
          hasError
        } = _ref4;
        return hasError;
      });
    }
    const populatedItems = items.map(item => buildPopulatedItem(item, getLabelsResponse.idsToEntityMap, getPriceVariantsResponse.idsToEntityMap, populateModifierGroupsResponse.idsToEntityMap));
    return {
      data: populatedItems,
      hasError: false
    };
  }
}
//# sourceMappingURL=PopulatedItemClient.js.map